// Core
import {useCallback, useMemo} from 'react';
import {useHistory} from 'react-router-dom';

// Chakra
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormHelperText,
    FormLabel,
    Input,
    Text,
    Textarea,
    useDisclosure
} from '@chakra-ui/react';

// Components
import {PermissionsActiveIcon} from 'components/Icons/Icons';
import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import Card from 'components/Card/Card';
import ErrorCard from 'components/Errors';
import Attention from 'components/Modals/Attention.js';
import PermissionList from './PermissionList';
import usePermissionStatusByKey from '../../../../../hooks/usePermissionStatusByKey';

const RoleEditContent = ({
                             isLoadingButton,
                             newObject,
                             newObjectErrors,
                             setNewObject,
                             setNewObjectErrors,
                             handleCreateObject,
                             handleDelete,
                             errorCard
                         }) => {

    const isValidInputName = useMemo(() => !newObjectErrors.includes('name'), [newObjectErrors]);
    const isValidInputDescription = useMemo(() => !newObjectErrors.includes('description'), [newObjectErrors]);

    const setData = useCallback((maxValue, target, key) => {
        if (target.length < maxValue) {
            setNewObjectErrors([...newObjectErrors, key]);
        } else {
            setNewObjectErrors(newObjectErrors.filter(error => error !== key));
        }
        setNewObject({...newObject, [key]: target});
    }, [newObject, newObjectErrors, setNewObject, setNewObjectErrors]);

    const handleNewName = useCallback((e) => {
        setData(1, e.target.value, 'name');
    }, [setData]);
    const handleNewDescription = useCallback((e) => {
        setData(0, e.target.value, 'description');
    }, [setData]);

    const history = useHistory();
    const handleCancelClick = useCallback(() => {
        setNewObject(null);
        history.push('/permissions/roles');
    }, [history, setNewObject]);

    const {isOpen, onOpen, onClose} = useDisclosure();
    const attentionModal = async () => {
        onOpen();
    };

    const isDeleteRole = usePermissionStatusByKey('DELETE:/admin/roles/:id');
    const isCreateRole = usePermissionStatusByKey('POST:/admin/roles');
    const isUpdateRole = usePermissionStatusByKey('PATCH:/admin/roles/:id');

    return (
        <Box zIndex="9" position="relative" className={isLoadingButton ? 'freeze' : ''}>
            {
                newObject.id ? (
                    <Breadcrumbs icon={PermissionsActiveIcon}
                                 target='Roles'
                                 url='#/permissions/roles'
                                 current={newObject.options?.type === 'header' ? 'Edit header' : 'Edit role'}
                    />
                ) : (
                    <Breadcrumbs
                        icon={PermissionsActiveIcon}
                        target='Roles'
                        url='#/permissions/roles'
                        current={newObject.options?.type === 'header' ? 'New header' : 'New role'}
                    />
                )
            }

            <Flex gap="16px" position="absolute" mt="-92px" right="0">
                {!newObject.id &&
                    <Button variant="ghost-md" minW="120px" onClick={() => handleCancelClick()}>Cancel</Button>}
                {newObject.id && isDeleteRole && <Button
                    variant="ghost-md"
                    w="120px"
                    disabled={(newObject?.name === 'Manager' || newObject?.name === 'Partner')}
                    onClick={attentionModal}>Delete</Button>}

                {(newObject.id ? isUpdateRole : isCreateRole) &&
                    <Button isLoading={isLoadingButton}
                            variant="submit-md" w="120px"
                            onClick={handleCreateObject}>{newObject.id ? 'Confirm' : 'Create'}</Button>}

            </Flex>

            {errorCard && (<ErrorCard error={errorCard} maxW="839px"/>)}

            <Box maxW="840px">
                <Text variant="title-lg" mt="28px" mb="14px">Details</Text>
                <Card padding="24px" mb="40px">
                    <FormControl variant="floating" id="name" mb="16px">
                        <Input disabled={newObject.id ? true : false} value={newObject.name} type='text' placeholder=''
                               autoComplete="off" mb='24px' onChange={handleNewName}
                               className={isValidInputName ? 'input-valid' : 'input-invalid'}/>
                        <FormLabel htmlFor="name"
                                   className={isValidInputName ? 'input-valid' : 'input-invalid'}>Name</FormLabel>
                        <FormHelperText className={isValidInputName ? 'hidden' : 'input-error'}>Input
                            invalid</FormHelperText>
                        <Text bottom="4px" pl="8px" opacity="0.6" position="absolute" variant="text-sm" color="#9fabcb">*This
                            field cannot be edited after creation to ensure system integrity and optimization.</Text>
                    </FormControl>

                    <FormControl variant="floating" id="description">
                        <Textarea
                            h={{base: '150px !important'}}
                            resize="none" value={newObject.description} type='text' placeholder='' autoComplete="off"
                            mb='28px' onChange={handleNewDescription}
                            className={isValidInputDescription ? 'input-valid' : 'input-invalid'}/>
                        <FormLabel htmlFor="description"
                                   className={isValidInputDescription ? 'input-valid' : 'input-invalid'}>Description</FormLabel>
                        <FormHelperText className={isValidInputDescription ? 'hidden' : 'input-error'}>Input
                            invalid</FormHelperText>
                    </FormControl>

                </Card>

                <Text variant="title-lg" mt="28px" mb="14px">Permissions</Text>
                <Card padding="0 24px" mb="40px">
                    <PermissionList newObject={newObject} setNewObject={setNewObject}/>
                </Card>

                <Attention
                    variant="delete-role"
                    handle={() => handleDelete(newObject)}
                    isOpen={isOpen}
                    onOpen={onOpen}
                    onClose={onClose}
                />
            </Box>
        </Box>
    );
};

export default RoleEditContent;